import { ConnectorInfo } from '@blaze-shared/api-contracts-domain';
import { BaseSchema } from '@blaze-shared/schema-domain';
import { DeleteWorkflowNode } from '@blaze-shared/workflow-domain';

import {
  DATASET_SOURCE_ID,
  ERROR_MESSAGE_SOURCE_ID,
  SUCCESS_MESSAGE_SOURCE_ID,
  WorkflowError,
} from '../../errors';
import {
  getColumnReferencesFromQueryNode,
  getNamedInputReferencesFromQueryNode,
  getParamReferencesFromQueryNode,
  getTagReferencesFromQueryNode,
} from '../../references/get-references-from-query-node';
import {
  getColumnReferencesFromString,
  getNamedInputReferencesFromString,
  getParamReferencesFromString,
  getTagReferencesFromString,
} from '../../references/get-references-from-string';
import {
  WorkflowNodeColumnReference,
  WorkflowNodeNamedInputReference,
  WorkflowNodeParamReference,
  WorkflowNodeTagReference,
} from '../../references/references.models';
import { BaseWorkflowNode, WorkflowNode } from '../../registry/node.registry';
import { validateDataset } from '../_validations/dataset';

@WorkflowNode('Delete')
export class DeleteNode extends BaseWorkflowNode<DeleteWorkflowNode> {
  static readonly DATASET_SOURCE_ID = DATASET_SOURCE_ID;
  static readonly SUCCESS_MESSAGE_SOURCE_ID = SUCCESS_MESSAGE_SOURCE_ID;
  static readonly ERROR_MESSAGE_SOURCE_ID = ERROR_MESSAGE_SOURCE_ID;

  static readonly CONDITION_SOURCE_ID = 'condition';
  static readonly CONFIRMATION_MESSAGE_SOURCE_ID = 'confirmation-message';

  getColumnReferences(schemas: Map<string | symbol, BaseSchema>): WorkflowNodeColumnReference[] {
    return [
      ...getColumnReferencesFromString(
        this.node.condition,
        schemas,
        DeleteNode.CONDITION_SOURCE_ID,
      ),
      ...getColumnReferencesFromString(
        this.node.successMessage,
        schemas,
        DeleteNode.SUCCESS_MESSAGE_SOURCE_ID,
      ),
      ...getColumnReferencesFromString(
        this.node.errorMessage,
        schemas,
        DeleteNode.ERROR_MESSAGE_SOURCE_ID,
      ),
      ...getColumnReferencesFromString(
        this.node.confirmationMessage,
        schemas,
        DeleteNode.CONFIRMATION_MESSAGE_SOURCE_ID,
      ),
      ...getColumnReferencesFromQueryNode(this.node, schemas),
    ];
  }

  getTagReferences(schemas: Map<string | symbol, BaseSchema>): WorkflowNodeTagReference[] {
    return [
      ...getTagReferencesFromString(this.node.condition, schemas, DeleteNode.CONDITION_SOURCE_ID),
      ...getTagReferencesFromString(
        this.node.successMessage,
        schemas,
        DeleteNode.SUCCESS_MESSAGE_SOURCE_ID,
      ),
      ...getTagReferencesFromString(
        this.node.errorMessage,
        schemas,
        DeleteNode.ERROR_MESSAGE_SOURCE_ID,
      ),
      ...getTagReferencesFromString(
        this.node.confirmationMessage,
        schemas,
        DeleteNode.CONFIRMATION_MESSAGE_SOURCE_ID,
      ),
      ...getTagReferencesFromQueryNode(this.node, schemas),
    ];
  }

  getParamReferences(
    schemas: Map<string | symbol, BaseSchema>,
    connectors: ConnectorInfo[],
  ): WorkflowNodeParamReference[] {
    return [
      ...getParamReferencesFromString(
        this.node.condition,
        schemas,
        connectors,
        DeleteNode.CONDITION_SOURCE_ID,
      ),
      ...getParamReferencesFromString(
        this.node.successMessage,
        schemas,
        connectors,
        DeleteNode.SUCCESS_MESSAGE_SOURCE_ID,
      ),
      ...getParamReferencesFromString(
        this.node.errorMessage,
        schemas,
        connectors,
        DeleteNode.ERROR_MESSAGE_SOURCE_ID,
      ),
      ...getParamReferencesFromString(
        this.node.confirmationMessage,
        schemas,
        connectors,
        DeleteNode.CONFIRMATION_MESSAGE_SOURCE_ID,
      ),
      ...getParamReferencesFromQueryNode(this.node, schemas, connectors),
    ];
  }

  getNamedInputReferences(
    schemas: Map<string | symbol, BaseSchema>,
  ): WorkflowNodeNamedInputReference[] {
    return [
      ...getNamedInputReferencesFromString(
        this.node.condition,
        schemas,
        DeleteNode.CONDITION_SOURCE_ID,
      ),
      ...getNamedInputReferencesFromString(
        this.node.successMessage,
        schemas,
        DeleteNode.SUCCESS_MESSAGE_SOURCE_ID,
      ),
      ...getNamedInputReferencesFromString(
        this.node.errorMessage,
        schemas,
        DeleteNode.ERROR_MESSAGE_SOURCE_ID,
      ),
      ...getNamedInputReferencesFromString(
        this.node.confirmationMessage,
        schemas,
        DeleteNode.CONFIRMATION_MESSAGE_SOURCE_ID,
      ),
      ...getNamedInputReferencesFromQueryNode(this.node, schemas),
    ];
  }

  override validate(
    schemas: Map<string | symbol, BaseSchema>,
    connectors: ConnectorInfo[],
  ): WorkflowError<any>[] {
    const errors = super.validate(schemas, connectors);
    return [
      ...errors,
      ...validateDataset(this.workflowId, this.node.id, this.node.datasetId, schemas),
    ];
  }
}
